import { apiClient } from "./ApiClient";
import { Room, Equipment, SearchRoomsParams } from "../types";
import { formatDateTimeForApi } from "../utils/dateUtils";
import { normalizeImageUrl } from "../utils/imageUtils";

// API response types (internal)
interface ApiRoom {
  id: number;
  name: string;
  capacity: number;
  zone_id?: number;
  zone?: { id: number; name: string; floor_number?: number };
  office?: { id: number; name: string };
  floor?: string;
  equipment?: string[];
  meeting_room_type: "bookableRoom" | "unbookableRoom";
  photo_url?: string;
  email?: string;
  timezone: string;
}

interface ApiEquipment {
  id: number;
  name: string;
  equipment_category?: string;
}

interface ApiRoomResponse {
  data: ApiRoom[];
}

interface ApiEquipmentResponse {
  data: ApiEquipment[];
}

interface ApiBookingResponse {
  data: { id: string };
}

export class RoomService {
  static async searchRooms(params: SearchRoomsParams): Promise<Room[]> {
    const queryParams = this.buildSearchParams(params);
    const endpoint = `/meeting-rooms/suggestions?${queryParams.toString()}`;

    const response = await apiClient.get<ApiRoomResponse>(endpoint);
    const rooms = response.data.map(this.mapApiRoomToRoom);

    return rooms.sort((a, b) => a.name.localeCompare(b.name));
  }

  static async getEquipmentOptions(): Promise<Equipment[]> {
    const response = await apiClient.get<ApiEquipmentResponse>("equipments");

    return response.data.map((eq) => ({
      id: eq.id.toString(),
      name: eq.name,
    }));
  }

  private static buildSearchParams(params: SearchRoomsParams): URLSearchParams {
    const { startDateTime, endDateTime } = formatDateTimeForApi(
      params.startTime,
      params.endTime
    );

    const queryParams = new URLSearchParams({
      start_date: startDateTime,
      end_date: endDateTime,
      capacity: (params.capacity || 1).toString(),
    });

    if (params.equipment && params.equipment.length > 0) {
      params.equipment.forEach((id) => {
        queryParams.append("filters[equipment][]", id);
      });
    }

    if (params.officeId) {
      queryParams.set("office_id", params.officeId);
    }

    return queryParams;
  }

  static async createDeskareBooking(
    roomId: string,
    startDate: Date,
    endDate: Date,
    options?: { name?: string; attendees?: string[] }
  ): Promise<{ bookingId: string }> {
    const { startDateTime, endDateTime } = formatDateTimeForApi(
      startDate.toISOString(),
      endDate.toISOString()
    );
    const endpoint = `/meeting-rooms/${roomId}/bookings`;
    const response = await apiClient.post<ApiBookingResponse>(endpoint, {
      start_datetime: startDateTime,
      end_datetime: endDateTime,
      add_to_calendar: false,
      ...(options?.name && { name: options.name }),
      ...(options?.attendees?.length && { attendees: options.attendees }),
    });
    return { bookingId: response.data.id };
  }

  static async cancelReservation(bookingId: string): Promise<void> {
    // TODO: Implement backend API call to cancel reservation
    // For now, this is a placeholder that will be implemented when backend endpoint is ready
    const endpoint = `/bookings/${bookingId}/cancel`;
    await apiClient.post(endpoint, {});
  }

  private static mapApiRoomToRoom(apiRoom: ApiRoom): Room {
    return {
      id: apiRoom.id.toString(),
      name: apiRoom.name,
      capacity: apiRoom.capacity,
      zone: apiRoom.zone?.name || "",
      floor: apiRoom.floor || "",
      officeId: apiRoom.office?.id?.toString(),
      officeName: apiRoom.office?.name,
      equipment: apiRoom.equipment || [],
      available: apiRoom.meeting_room_type === "bookableRoom",
      imageUrl: normalizeImageUrl(apiRoom.photo_url),
      email: apiRoom.email || undefined,
      timezone: apiRoom.timezone,
    };
  }
}
